import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

import api from "../api/axiosConfig";
import { useGameModal } from "../context/GameModalContext";
import { getAvatarUrl } from "../utils/getAvatarUrl";
import { extractApiList } from "../api/response";
import logo from "../assets/logo/xphub-logo.png";
import ContextualAuthLink from "./ContextualAuthLink";

// Lucide icons
import { Search, UserRound, X, Mail, Bell, Activity, Star, LibraryBig, Heart, Trash2, Users } from "lucide-react";

/* ===== Utils ===== */
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

const norm = (v) => (typeof v === "string" ? v.trim().toLowerCase() : v);

// Fallbacks para ID local
const getMyIdFromStorageOrJWT = () => {
  try {
    const stored = localStorage.getItem("userId");
    return stored ? String(stored) : null;
  } catch {
    return null;
  }
};

const strId = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v;
  return String(v._id || v.id || v.userId || "");
};

const isPendingReq = (req) => {
  const s = String(req?.status || req?.state || req?.statusName || "").toLowerCase();
  if (s) return s === "pending" || s === "pendiente";
  if (typeof req?.accepted === "boolean") return req.accepted === false && req.rejected !== true;
  if (typeof req?.isAccepted === "boolean") return req.isAccepted === false && req.isRejected !== true;
  return false;
};

const isIncomingFor = (req, myId) => {
  const receiver =
    strId(req?.receiverId) ||
    strId(req?.to) ||
    strId(req?.receiver) ||
    strId(req?.recipientId);
  return myId && receiver && String(receiver) === String(myId);
};

const relativeTime = (value) => {
  if (!value) return "";
  const now = Date.now();
  const ts = new Date(value).getTime();
  if (!Number.isFinite(ts)) return "";
  const diff = Math.max(0, now - ts);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "Ahora";
  if (min < 60) return `Hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `Hace ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `Hace ${d} d`;
  return new Date(ts).toLocaleDateString();
};

const describeNotificationActivity = (item) => {
  const t = String(item?.type || "").toLowerCase();
  const actor = item?.user?.username || "Un usuario";
  const title = item?.target?.title || "un juego";
  if (t.startsWith("message.unread")) {
    const unread = Number(item?.meta?.unread || 0);
    if (unread > 1) return `${actor} te envió ${unread} mensajes`;
    return `${actor} te envió un mensaje`;
  }
  if (t.startsWith("message.moderated_delete")) {
    const reason = String(item?.meta?.reason || "").trim();
    return reason
      ? `Moderación eliminó uno de tus mensajes (${reason})`
      : "Moderación eliminó uno de tus mensajes";
  }

  if (t === "session.invite") {
    const inviter = item?.meta?.inviterUsername || "Un usuario";
    const gameTitle = item?.meta?.gameTitle || item?.target?.title || "un juego";
    return `${inviter} te ha invitado a una sesión de ${gameTitle}`;
  }

  if (t.startsWith("review.")) return `${actor} publicó una reseña de ${title}`;
  if (t.startsWith("rating.")) return `${actor} valoró ${title}`;
  if (t.startsWith("library.update")) return `${actor} actualizó su librería con ${title}`;
  if (t.startsWith("library.remove")) return `${actor} quitó ${title} de su librería`;
  if (t.startsWith("list.")) return `${actor} actualizó una lista con ${title}`;
  if (t.startsWith("game.session")) return `${actor} registró una sesión en ${title}`;
  if (t.startsWith("game.like")) return `${actor} dio like a ${title}`;
  if (t.startsWith("game.unlike")) return `${actor} quitó like en ${title}`;
  if (t.startsWith("comment.")) return `${actor} comentó sobre ${title}`;
  return `${actor} tuvo actividad en ${title}`;
};

const ActivityTypeIcon = ({ type }) => {
  const t = String(type || "").toLowerCase();
  if (t.startsWith("message.unread")) return <Mail size={14} color="#00b3ff" />;
  if (t.startsWith("message.moderated_delete")) return <Mail size={14} color="#fb923c" />;
  if (t === "session.invite") return <Users size={14} color="#fcb14a" />;
  if (t.startsWith("review.")) return <Star size={14} color="#22c55e" />;
  if (t.startsWith("library.")) return <LibraryBig size={14} color="#00b3ff" />;
  if (t.startsWith("game.like") || t.startsWith("game.unlike")) return <Heart size={14} color="#ff3b30" />;
  return <Activity size={14} color="#9ca3af" />;
};

const SILENT_API_CONFIG = {
  silentGlobalError: true,
  skipGlobalErrorToast: true,
};

function Header({ profile, isAuthenticated, onLogout, onOpenQuickSession, quickSessionOpen = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // 🔐 ID del usuario (muy robusto: perfil → storage/JWT)
  const myId = useMemo(() => {
    return String(profile?._id || profile?.id || getMyIdFromStorageOrJWT() || "");
  }, [profile]);

  // Roles de staff
  const profileRole = norm(profile?.role);
  const storageRole = norm(localStorage.getItem("role"));
  const isSuperadmin = profileRole === "superadmin" || storageRole === "superadmin";
  const isAdmin =
    isSuperadmin ||
    profileRole === "admin" ||
    storageRole === "admin";
  const isModerator =
    isAdmin ||
    profileRole === "mod" ||
    storageRole === "mod";
  const staffRoleLabel = isSuperadmin ? "SUPERADMIN" : isAdmin ? "ADMIN" : isModerator ? "MOD" : "";

  // Altura dinámica del header
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(64);

  useEffect(() => {
    const updateHeaderHeight = () => {
      const h = headerRef.current?.getBoundingClientRect().height;
      if (h && Math.abs(h - headerHeight) > 0.5) setHeaderHeight(h);
    };
    updateHeaderHeight();
    window.addEventListener("resize", updateHeaderHeight);
    return () => window.removeEventListener("resize", updateHeaderHeight);
  }, [headerHeight]);

  // Glass header: fondo transparente → opaco con blur al hacer scroll
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Estados UI
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [gamesMenuOpen, setGamesMenuOpen] = useState(false);
  // Búsqueda
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);

  // Focus visual login
  const [loginFocus, setLoginFocus] = useState(false);

  // Contadores
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  const [pendingFriends, setPendingFriends] = useState(0); // 👈 solo ENTRANTES PENDIENTES
  const [pendingInvites, setPendingInvites] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationFeed, setNotificationFeed] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [clearingNotifications, setClearingNotifications] = useState(false);

  // Refs
  const userMenuRef = useRef(null);
  const mobileMenuWrapRef = useRef(null);
  const searchRef = useRef(null);
  const notificationsRef = useRef(null);
  const abortRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const pollUnreadRef = useRef(null);
  const pollPendingRef = useRef(null);
  const pollNotificationsRef = useRef(null);

  const { openModal } = useGameModal();

  useEffect(() => {
    const shouldLock = isMobile && (userMenuOpen || mobileMenuOpen || searchOpen || notificationsOpen || quickSessionOpen);
    document.body.style.overflow = shouldLock ? "hidden" : "";
  }, [isMobile, userMenuOpen, mobileMenuOpen, searchOpen, notificationsOpen, quickSessionOpen]);

  const closeAll = useCallback(() => {
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
    setSearchOpen(false);
    setNotificationsOpen(false);
    setGamesMenuOpen(false);
    setSearchResults([]);
  }, []);

  const openQuickSession = useCallback(() => {
    closeAll();
    onOpenQuickSession?.();
  }, [closeAll, onOpenQuickSession]);

  const handleProfileAreaClick = useCallback(() => {
    if (isMobile) {
      setUserMenuOpen((v) => !v);
      return;
    }
    setUserMenuOpen(false);
    navigate("/user-profile");
  }, [isMobile, navigate]);

  useEffect(() => {
    closeAll();
  }, [location.pathname, closeAll]);

  useEffect(() => {
    const handleClick = (e) => {
      if (userMenuOpen && userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
      if (mobileMenuOpen && mobileMenuWrapRef.current && !mobileMenuWrapRef.current.contains(e.target)) {
        setMobileMenuOpen(false);
      }
      if (
        notificationsOpen &&
        notificationsRef.current &&
        !notificationsRef.current.contains(e.target)
      ) {
        setNotificationsOpen(false);
      }
      if (!isMobile && searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchResults([]);
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [userMenuOpen, mobileMenuOpen, notificationsOpen, isMobile]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") closeAll();
      if (e.key === "/" && !searchOpen && !isMobile) {
        const t = e.target;
        const typing =
          t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
        if (!typing) {
          e.preventDefault();
          const input = document.getElementById("global-search-input");
          if (input) input.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeAll, searchOpen, isMobile]);

  // Logout (SweetAlert2 oscuro)
  const handleLogoutClick = async () => {
    const result = await Swal.fire({
      title: "¿Cerrar sesión?",
      text: "Saldrás de tu cuenta en este dispositivo.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#000",
      cancelButtonColor: "#444",
      confirmButtonText: "Sí, cerrar sesión",
      cancelButtonText: "Cancelar",
      background: "#1e1e1e",
      color: "#fff",
    });
    if (!result.isConfirmed) return;

try {
  if (typeof onLogout === "function") onLogout();
  closeAll();

  // Mejor granular (evita borrar cosas globales)
  localStorage.removeItem("userId");
  localStorage.removeItem("role");
  localStorage.removeItem("isAdmin");
  sessionStorage.clear();

  await Swal.fire({
    title: "Sesión cerrada",
    text: "Has salido de tu cuenta correctamente.",
    icon: "success",
    confirmButtonColor: "#000",
    background: "#1e1e1e",
    color: "#fff",
  });

  navigate("/", { replace: true });
} catch (err) {
      console.error("Error al cerrar sesión:", err);
      Swal.fire({
        title: "Error",
        text: "No se pudo cerrar la sesión. Inténtalo de nuevo.",
        icon: "error",
        confirmButtonColor: "#c62828",
        background: "#1e1e1e",
        color: "#fff",
      });
    }
  };

  // Búsqueda

  const fetchGames = useCallback(
    async (query) => {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();

      if (!query || query.length < 3) {
        setSearchResults([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const res = await api.get("/games/search", {
          params: {
            query,
            sort: "popularity",
            page_size: 8,
          },
          signal: abortRef.current.signal,
        });
        const data = extractApiList(res.data);
        setSearchResults(data.slice(0, 8));
      } catch (err) {
        if (axios.isCancel(err) || err.name === "CanceledError") return;
        console.error("Error buscando juegos:", err.message);
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const handleInputChange = (e) => {
    const v = e.target.value;
    setSearchQuery(v);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => fetchGames(v), 250);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    setSearchResults([]);
    if (isMobile) setSearchOpen(false);
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  /* ================= Unread mensajes ================= */
  const fetchUnreadMessages = useCallback(async () => {
    try {
      if (!isAuthenticated) return;

      const res = await api.get("/messages/conversations", SILENT_API_CONFIG);
      const items = res?.data?.items || [];
      const total = items.reduce((acc, c) => acc + (Number(c.unread) || 0), 0);
      setUnreadMsgs(total);
    } catch {
      // silencioso
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadMsgs(0);
      return;
    }
    fetchUnreadMessages();
    pollUnreadRef.current && clearInterval(pollUnreadRef.current);
    pollUnreadRef.current = setInterval(fetchUnreadMessages, 30000);
    return () => pollUnreadRef.current && clearInterval(pollUnreadRef.current);
  }, [isAuthenticated, fetchUnreadMessages]);

  /* =========== Solicitudes de amistad PENDIENTES (entrantes) =========== */
  const fetchPendingFriendRequests = useCallback(async () => {
    try {
      if (!isAuthenticated || !myId) {
        setPendingFriends(0);
        return;
      }

      // 1) Intento principal: endpoint con filtros (si existe)
      try {
        const res = await api.get("/friends/requests?scope=incoming&status=pending", SILENT_API_CONFIG);
        const d = res?.data ?? {};
        const arrays = [d.items, d.requests, d.pending, d.pendingRequests, d.incoming, d.incomingRequests, d.data].filter(Array.isArray);
        const raw = arrays.length > 0 ? arrays[0] : [];
        let count;
        if (raw.length > 0) {
          const needsFilter = raw.some((r) => r?.status === undefined && r?.state === undefined && r?.accepted === undefined && r?.isAccepted === undefined);
          count = needsFilter ? raw.filter((r) => isPendingReq(r) && isIncomingFor(r, myId)).length : raw.length;
        } else {
          count = 0;
        }
        setPendingFriends(count);
        if (count > 0) return;
      } catch {
        // sigue a 2º intento
      }

      // 2) Alternativa: /friends/pending
      try {
        const res2 = await api.get("/friends/pending", SILENT_API_CONFIG);
        const d2 = res2?.data ?? {};
        const arrays2 = [d2.items, d2.requests, d2.pending, d2.pendingRequests, d2.incoming, d2.incomingRequests, d2.data].filter(Array.isArray);
        const raw2 = arrays2.length > 0 ? arrays2[0] : [];
        const count2 = raw2.filter((r) => isPendingReq(r) && isIncomingFor(r, myId)).length;
        setPendingFriends(count2);
        if (count2 > 0) return;
      } catch {
        // sigue a fallback
      }

      // 3) Fallback genérico: /friends/requests (sin filtros)
      try {
        const res3 = await api.get("/friends/requests", SILENT_API_CONFIG);
        const d3 = res3?.data ?? {};
        const arrays3 = [d3.pending, d3.pendingRequests, d3.incoming, d3.incomingRequests, d3.requests, d3.items, d3.data].filter(Array.isArray);
        const raw3 = arrays3.length > 0 ? arrays3[0] : [];
        const count3 = raw3.filter((r) => isPendingReq(r) && isIncomingFor(r, myId)).length;
        setPendingFriends(count3);
      } catch {
        setPendingFriends(0);
      }
    } catch {
      setPendingFriends(0);
    }
  }, [isAuthenticated, myId]);

  useEffect(() => {
    if (!isAuthenticated) {
      setPendingFriends(0);
      return;
    }
    fetchPendingFriendRequests();
    pollPendingRef.current && clearInterval(pollPendingRef.current);
    pollPendingRef.current = setInterval(fetchPendingFriendRequests, 30000);
    return () => pollPendingRef.current && clearInterval(pollPendingRef.current);
  }, [isAuthenticated, fetchPendingFriendRequests]);

  /* =========== Invitaciones pendientes a sesiones =========== */
  const fetchPendingSessionInvites = useCallback(async () => {
    try {
      if (!isAuthenticated) {
        setPendingInvites([]);
        return [];
      }
      const resInvites = await api.get("/activity/my?type=session.invite&limit=20", SILENT_API_CONFIG);
      const items = Array.isArray(resInvites?.data?.items) ? resInvites.data.items : [];
      setPendingInvites(items);
      return items;
    } catch {
      setPendingInvites([]);
      return [];
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setPendingInvites([]);
      return;
    }
    fetchPendingSessionInvites();
    const interval = setInterval(fetchPendingSessionInvites, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchPendingSessionInvites]);

  /* ================= Notificaciones (feed) ================= */
  const fetchNotificationFeed = useCallback(async () => {
    try {
      if (!isAuthenticated) {
        setNotificationFeed([]);
        setPendingInvites([]);
        return;
      }

      setLoadingNotifications(true);

      let items = [];
      let personalModeration = [];
      let messageNotifications = [];
      let sessionInviteNotifications = [];

      try {
        const resFollowing = await api.get(
          "/activity/feed?scope=following&limit=8&notifications=1",
          SILENT_API_CONFIG
        );
        items = Array.isArray(resFollowing?.data?.items) ? resFollowing.data.items : [];
      } catch {
        items = [];
      }

      if (!items.length) {
        try {
          const resFriends = await api.get(
            "/activity/feed?scope=friends&limit=8&notifications=1",
            SILENT_API_CONFIG
          );
          items = Array.isArray(resFriends?.data?.items) ? resFriends.data.items : [];
        } catch {
          items = [];
        }
      }

      try {
        const resPersonal = await api.get(
          "/activity/my?type=message&limit=8&notifications=1",
          SILENT_API_CONFIG
        );
        const mine = Array.isArray(resPersonal?.data?.items) ? resPersonal.data.items : [];
        personalModeration = mine.filter((entry) =>
          String(entry?.type || "").toLowerCase().startsWith("message.moderated_delete")
        );
      } catch {
        personalModeration = [];
      }

      try {
        const resConversations = await api.get("/messages/conversations", SILENT_API_CONFIG);
        const convItems = Array.isArray(resConversations?.data?.items) ? resConversations.data.items : [];
        messageNotifications = convItems
          .filter((entry) => Number(entry?.unread || 0) > 0)
          .slice(0, 6)
          .map((entry) => ({
            _id: `msg-unread-${String(entry?._id || "")}-${String(entry?.lastMessageAt || "")}`,
            type: "message.unread",
            createdAt: entry?.lastMessageAt || new Date().toISOString(),
            user: {
              _id: entry?.otherUser?._id || "",
              username: entry?.otherUser?.username || "Usuario",
              avatar: entry?.otherUser?.avatar || "",
            },
            target: {
              title: entry?.lastMessageText || "Nuevo mensaje",
            },
            meta: {
              unread: Number(entry?.unread || 0),
              conversationId: entry?._id || "",
              userId: entry?.otherUser?._id || "",
              username: entry?.otherUser?.username || "Usuario",
              avatar: entry?.otherUser?.avatar || "",
            },
          }));
      } catch {
        messageNotifications = [];
      }

      sessionInviteNotifications = await fetchPendingSessionInvites();

      const merged = [...messageNotifications, ...sessionInviteNotifications, ...personalModeration, ...items]
        .filter(Boolean)
        .sort((a, b) => new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime());
      const seen = new Set();
      const deduped = merged.filter((entry) => {
        const key = String(entry?._id || `${entry?.type || "x"}-${entry?.createdAt || "0"}`);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      setNotificationFeed(deduped.slice(0, 8));
    } catch {
      setNotificationFeed([]);
    } finally {
      setLoadingNotifications(false);
    }
  }, [fetchPendingSessionInvites, isAuthenticated]);

  const handleClearNotifications = useCallback(async () => {
    if (!isAuthenticated || clearingNotifications) return;

    const result = await Swal.fire({
      title: "¿Limpiar notificaciones?",
      text: "Se limpiará la actividad reciente del panel. Los mensajes sin leer no se borran.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#000",
      cancelButtonColor: "#444",
      confirmButtonText: "Sí, limpiar",
      cancelButtonText: "Cancelar",
      background: "#1e1e1e",
      color: "#fff",
    });
    if (!result.isConfirmed) return;

    try {
      setClearingNotifications(true);
      await api.post("/activity/notifications/clear", {});
      setNotificationFeed([]);
      await Swal.fire({
        title: "Notificaciones limpiadas",
        icon: "success",
        timer: 1300,
        showConfirmButton: false,
        background: "#1e1e1e",
        color: "#fff",
      });
    } catch (err) {
      console.error("Error limpiando notificaciones:", err);
      Swal.fire({
        title: "No se pudo limpiar",
        text: "Inténtalo de nuevo en unos segundos.",
        icon: "error",
        confirmButtonColor: "#c62828",
        background: "#1e1e1e",
        color: "#fff",
      });
    } finally {
      setClearingNotifications(false);
    }
  }, [clearingNotifications, isAuthenticated]);

  const handleNotificationItemClick = useCallback(
    (item) => {
      setNotificationsOpen(false);
      const type = String(item?.type || "").toLowerCase();
      if (type.startsWith("message.unread")) {
        const userId = String(item?.meta?.userId || item?.user?._id || item?.user?.id || "");
        const username = String(item?.meta?.username || item?.user?.username || "Usuario");
        const avatar = String(item?.meta?.avatar || item?.user?.avatar || "");
        if (userId) {
          navigate("/messages", {
            state: {
              userId,
              username,
              avatar,
            },
          });
          return;
        }
        navigate("/messages");
        return;
      }
      navigate("/feed");
    },
    [navigate]
  );

  const handleSessionInviteResponse = useCallback(async (item, accepted) => {
    const sessionId = String(item?.meta?.sessionId || "");
    if (!sessionId) return;
    try {
      await api.post(`/game-sessions/${encodeURIComponent(sessionId)}/invite/respond`, { accepted });
      setPendingInvites((prev) => prev.filter((entry) => String(entry?._id) !== String(item?._id)));
      setNotificationFeed((prev) => prev.filter((entry) => String(entry?._id) !== String(item?._id)));
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setNotificationFeed([]);
      setNotificationsOpen(false);
      return;
    }
    if (!notificationsOpen) {
      if (pollNotificationsRef.current) clearInterval(pollNotificationsRef.current);
      return;
    }

    fetchNotificationFeed();
    if (pollNotificationsRef.current) clearInterval(pollNotificationsRef.current);
    pollNotificationsRef.current = setInterval(fetchNotificationFeed, 45000);
    return () => pollNotificationsRef.current && clearInterval(pollNotificationsRef.current);
  }, [isAuthenticated, notificationsOpen, fetchNotificationFeed]);

  /* ================= Estilos ================= */
  const notificationCount = unreadMsgs + pendingFriends + pendingInvites.length;

  const styles = {
    header: {
      display: location.pathname === "/login" && !location.state?.background ? "none" : "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: isMobile ? "8px 12px" : "8px 24px",
      position: "sticky",
      top: 0,
      zIndex: 50,
      transition: "background 180ms ease, border-color 180ms ease",
      background: scrolled
        ? "rgba(13,21,32,0.94)"
        : "rgba(13,21,32,0.55)",
      backdropFilter: scrolled ? "blur(18px)" : "blur(8px)",
      WebkitBackdropFilter: scrolled ? "blur(18px)" : "blur(8px)",
      borderBottom: "1px solid transparent",
    },
    brand: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    logo: {
      height: isMobile ? "64px" : "84px",
      width: "auto",
      objectFit: "contain",
      marginRight: isMobile ? "8px" : "24px",
      display: "block",
      alignSelf: "center",
      transform: isMobile ? "translateX(2px) scale(1.12)" : "translateX(8px) scale(1.22)",
      transformOrigin: "left center",
      willChange: "transform",
    },
    nav: { display: isMobile ? "none" : "flex", gap: 16, marginLeft: 10 },
    navLink: { color: "#fff", textDecoration: "none" },
    navDropdownWrap: {
      position: "relative",
      display: "inline-flex",
      alignItems: "center",
    },
    navDropdownMenu: {
      position: "absolute",
      top: "100%",
      left: 0,
      minWidth: 200,
      background: "#171717",
      border: "1px solid #2d2d2d",
      borderRadius: 10,
      padding: 8,
      boxShadow: "0 10px 24px rgba(0,0,0,0.35)",
      zIndex: 1200,
      display: "grid",
      gap: 4,
    },
    navDropdownItem: {
      color: "#fff",
      textDecoration: "none",
      padding: "8px 10px",
      borderRadius: 8,
      fontSize: 14,
      whiteSpace: "nowrap",
    },
    searchWrap: {
      display: isMobile ? "none" : "flex",
      alignItems: "center",
      gap: "8px",
      marginRight: isAuthenticated ? "6px" : "20px",
      position: "relative",
    },
    searchInput: {
      width: "220px",
      background: "transparent",
      border: "1px solid #444",
      borderRadius: "8px",
      height: 36,
      padding: "0 12px",
      margin: 0,
      lineHeight: 1,
      color: "#fff",
      fontSize: "14px",
      boxSizing: "border-box",
    },
    searchDropdown: {
      position: "absolute",
      top: "45px",
      left: 0,
      background: "#1e1e1e",
      borderRadius: "8px",
      width: "320px",
      maxHeight: "320px",
      overflowY: "auto",
      zIndex: 1000,
      boxShadow: "0 4px 10px rgba(0,0,0,0.6)",
    },
    iconBtn: {
      background: "#000",
      border: "1px solid #333",
      borderRadius: "8px",
      width: 36,
      height: 36,
      padding: 0,
      lineHeight: 0,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
    },
    quickSessionBtn: {
      background: "#101010",
      border: "1px solid #3b4d64",
      borderRadius: 8,
      height: 36,
      padding: "0 12px",
      cursor: "pointer",
      display: isMobile ? "none" : "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 7,
      color: "#fff",
      fontWeight: 800,
      marginRight: 8,
    },
    iconSvg: { display: "block" },
    avatarImg: {
      width: "35px",
      height: "35px",
      borderRadius: "50%",
      marginRight: "10px",
      objectFit: "cover",
    },
    userMenu: {
      position: "absolute",
      top: "100%",
      right: 0,
      background: "#1e1e1e",
      borderRadius: "8px",
      padding: "10px",
      boxShadow: "0 4px 8px rgba(0,0,0,0.4)",
      width: "max-content",
      minWidth: "180px",
      maxWidth: "240px",
      zIndex: 1000,
    },
    mobileBar: {
      display: isMobile ? "flex" : "none",
      alignItems: "center",
      gap: 10,
      marginLeft: "auto",
    },
    hamburgerMenu: {
      position: "fixed",
      top: headerHeight,
      left: 0,
      right: 0,
      background: "#121212",
      borderBottom: "1px solid #222",
      padding: "12px 16px",
      zIndex: 1001,
      opacity: mobileMenuOpen ? 1 : 0,
      transform: mobileMenuOpen ? "translateY(0px)" : "translateY(-8px)",
      visibility: mobileMenuOpen ? "visible" : "hidden",
      pointerEvents: mobileMenuOpen ? "auto" : "none",
      transition:
        "opacity 160ms ease, transform 200ms ease, visibility 0ms linear 160ms",
    },
    mobileNavList: {
      display: "grid",
      gap: 12,
      margin: 0,
      padding: 0,
      listStyle: "none",
    },
    mobileSubNavLink: {
      color: "#cbd8ea",
      textDecoration: "none",
      marginLeft: 14,
      fontSize: 14,
      display: "inline-block",
    },
    mobileSearchOverlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.9)",
      zIndex: 1000,
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "center",
      paddingTop: "12vh",
      paddingLeft: 16,
      paddingRight: 16,
      opacity: searchOpen ? 1 : 0,
      visibility: searchOpen ? "visible" : "hidden",
      pointerEvents: searchOpen ? "auto" : "none",
      transition: "opacity 200ms ease, visibility 0ms linear 200ms",
    },
    mobileSearchCard: {
      width: "100%",
      maxWidth: 560,
      background: "#181818",
      border: "1px solid #333",
      borderRadius: 12,
      padding: 12,
      transform: searchOpen ? "translateY(0px)" : "translateY(6px)",
      transition: "transform 200ms ease",
    },
    mobileSearchInput: {
      width: "100%",
      background: "transparent",
      border: "1px solid #444",
      borderRadius: "8px",
      padding: "12px 14px",
      margin: 0,
      color: "#fff",
      fontSize: "16px",
      marginBottom: 8,
    },
    mobileResult: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px",
      borderBottom: "1px solid #262626",
      cursor: "pointer",
    },
    loginLink: {
      color: "#fff",
      textDecoration: "none",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      fontWeight: "bold",
      fontSize: 14,
      padding: "6px 8px",
      borderRadius: 8,
      outline: "none",
      boxShadow: loginFocus ? "0 0 0 3px rgba(0,179,255,0.35)" : "none",
      transition: "box-shadow .18s ease",
    },
    badge: {
      background: "#fcb14a",
      color: "#000",
      borderRadius: 12,
      padding: "1px 7px",
      fontSize: 12,
      fontWeight: 800,
      marginLeft: 8,
      display: "inline-block",
      lineHeight: 1.4,
    },
    mailBtn: {
      position: "relative",
      background: "#000",
      border: "1px solid #333",
      borderRadius: 8,
      width: 36,
      height: 36,
      padding: 0,
      lineHeight: 0,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
      marginRight: 8,
    },
    mailBadge: {
      position: "absolute",
      top: -6,
      right: -6,
      background: "#e53935",
      color: "#fff",
      borderRadius: 999,
      minWidth: 18,
      height: 18,
      padding: "0 5px",
      fontSize: 11,
      fontWeight: 800,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: "2px solid #121212",
      lineHeight: 1,
    },
    notificationsPanel: {
      position: "absolute",
      top: "calc(100% + 10px)",
      right: 0,
      width: 360,
      maxHeight: "70vh",
      overflowY: "auto",
      background: "#161616",
      border: "1px solid #2b2b2b",
      borderRadius: 12,
      boxShadow: "0 16px 34px rgba(0,0,0,0.45)",
      zIndex: 1100,
      padding: 10,
    },
    notificationAction: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
      width: "100%",
      background: "#1f1f1f",
      border: "1px solid #303030",
      borderRadius: 10,
      color: "#fff",
      textDecoration: "none",
      padding: "10px 12px",
      marginBottom: 8,
      cursor: "pointer",
      fontSize: 14,
    },
    notificationFeedItem: {
      width: "100%",
      textAlign: "left",
      background: "#141414",
      border: "1px solid #2a2a2a",
      borderRadius: 10,
      color: "#fff",
      padding: "10px 12px",
      marginTop: 8,
      cursor: "pointer",
    },
  };

  /* ================= Render ================= */
  return (
    <header ref={headerRef} style={styles.header}>
      {/* Brand */}
      <div style={styles.brand}>
        <Link to="/" style={{ display: "flex", alignItems: "center", textDecoration: "none" }} aria-label="Inicio XPHub">
          <img src={logo} alt="XPHub Logo" style={styles.logo} />
        </Link>
      </div>

      {/* Navegación desktop */}
      <nav style={styles.nav} aria-label="Secciones principales">
        <div
          style={styles.navDropdownWrap}
          onMouseEnter={() => setGamesMenuOpen(true)}
          onMouseLeave={() => setGamesMenuOpen(false)}
        >
          <Link
            to="/search"
            style={styles.navLink}
            onFocus={(e) => (e.currentTarget.style.color = "#fcb14a")}
            onBlur={(e) => (e.currentTarget.style.color = "#fff")}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#fcb14a")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#fff")}
          >
            Juegos ▾
          </Link>
          <AnimatePresence>
            {gamesMenuOpen && (
              <motion.div
                key="games-menu"
                style={{ ...styles.navDropdownMenu, transformOrigin: "top center" }}
                initial={{ opacity: 0, y: -6, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -4, scale: 0.98 }}
                transition={{ type: "spring", duration: 0.2, bounce: 0 }}
              >
                <Link to="/search" style={styles.navDropdownItem}>
                  Explorar juegos
                </Link>
                <Link to="/genres" style={styles.navDropdownItem}>
                  Géneros
                </Link>
                <Link to="/platforms" style={styles.navDropdownItem}>
                  Plataformas
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        {isAuthenticated && (
          <Link
            to="/mi-xp"
            style={styles.navLink}
            onFocus={(e) => (e.currentTarget.style.color = "#fcb14a")}
            onBlur={(e) => (e.currentTarget.style.color = "#fff")}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#fcb14a")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#fff")}
          >
            Mi XP
          </Link>
        )}
        <Link
          to="/mundo-gaming"
          style={styles.navLink}
          onFocus={(e) => (e.currentTarget.style.color = "#fcb14a")}
          onBlur={(e) => (e.currentTarget.style.color = "#fff")}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#fcb14a")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#fff")}
        >
          Mundo Gaming
        </Link>
        <Link
          to="/communities"
          style={styles.navLink}
          onFocus={(e) => (e.currentTarget.style.color = "#fcb14a")}
          onBlur={(e) => (e.currentTarget.style.color = "#fff")}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#fcb14a")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#fff")}
        >
          Comunidad
        </Link>
        <Link
          to="/news"
          style={styles.navLink}
          onFocus={(e) => (e.currentTarget.style.color = "#fcb14a")}
          onBlur={(e) => (e.currentTarget.style.color = "#fff")}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#fcb14a")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#fff")}
        >
          Noticias
        </Link>
      </nav>

      <div style={{ flex: 1 }} />

      {/* Buscador desktop */}
      <div ref={searchRef} style={styles.searchWrap}>
        <form role="search" action="/search" method="get" onSubmit={handleSubmit} style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            id="global-search-input"
            type="text"
            name="q"
            placeholder="Buscar en XPHub..."
            value={searchQuery}
            onChange={handleInputChange}
            autoComplete="off"
            style={styles.searchInput}
            aria-label="Buscar en XPHub"
          />
          <button type="submit" style={styles.iconBtn} aria-label="Buscar">
            <Search size={18} style={styles.iconSvg} />
          </button>
        </form>

        {searchResults.length > 0 && (
          <div role="listbox" style={styles.searchDropdown} aria-label="Sugerencias de búsqueda">
            {loading ? (
              <p style={{ color: "#999", padding: "10px" }}>Buscando...</p>
            ) : (
              searchResults.map((game) => (
                <div
                  role="option"
                  aria-selected="false"
                  key={game.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "8px",
                    cursor: "pointer",
                    borderBottom: "1px solid #333",
                  }}
                  onClick={() => {
                    setSearchResults([]);
                    setSearchQuery("");
                    openModal({
                      id: game.id,
                      name: game.name,
                      background_image: game.image,
                      source: "igdb",
                    });
                  }}
                >
                  <img
                    src={game.image}
                    alt={game.name}
                    style={{ width: 40, height: 40, borderRadius: 4, objectFit: "cover" }}
                  />
                  <span style={{ color: "#fff", fontSize: 14 }}>
                    {game.name}{" "}
                    <span style={{ color: "#999" }}>
                      {game.released ? `(${String(game.released).slice(0, 4)})` : ""}
                    </span>
                  </span>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Acción rápida de sesión */}
      {isAuthenticated && (
        <button
          type="button"
          onClick={openQuickSession}
          style={styles.quickSessionBtn}
          title="Registrar sesión de juego"
        >
          <Activity size={16} color="#fcb14a" />
          Sesión
        </button>
      )}

      {/* Área usuario / login */}
      <div style={{ position: "relative" }}>
        {isAuthenticated ? (
          <div
            ref={userMenuRef}
            style={{ position: "relative" }}
            onMouseEnter={() => {
              if (!isMobile) setUserMenuOpen(true);
            }}
            onMouseLeave={() => {
              if (!isMobile) setUserMenuOpen(false);
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              {/* 🔔 Notificaciones */}
              <div ref={notificationsRef} style={{ position: "relative", marginRight: 8 }}>
                <button
                  type="button"
                  aria-label="Abrir notificaciones"
                  title="Notificaciones"
                  style={styles.mailBtn}
                  onClick={() => {
                    setNotificationsOpen((v) => !v);
                    setUserMenuOpen(false);
                  }}
                >
                  <Bell size={18} />
                  {notificationCount > 0 && (
                    <span style={styles.mailBadge}>
                      {notificationCount > 99 ? "99+" : notificationCount}
                    </span>
                  )}
                </button>

                <AnimatePresence>
                {notificationsOpen && (
                  <motion.div
                    key="notifications-panel"
                    style={{ ...styles.notificationsPanel, transformOrigin: "top right" }}
                    role="menu"
                    aria-label="Panel de notificaciones"
                    initial={{ opacity: 0, y: -8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -5, scale: 0.97 }}
                    transition={{ type: "spring", duration: 0.22, bounce: 0 }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 10,
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: 15,
                      }}
                    >
                      <span>Notificaciones</span>
                      <button
                        type="button"
                        onClick={() => setNotificationsOpen(false)}
                        style={{ ...styles.iconBtn, width: 28, height: 28, borderRadius: 8 }}
                        aria-label="Cerrar notificaciones"
                      >
                        <X size={14} />
                      </button>
                    </div>

                    {unreadMsgs > 0 && (
                      <Link
                        to="/messages"
                        style={styles.notificationAction}
                        onClick={() => setNotificationsOpen(false)}
                      >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                          <Mail size={15} color="#00b3ff" />
                          Mensajes sin leer
                        </span>
                        <strong>{unreadMsgs > 99 ? "99+" : unreadMsgs}</strong>
                      </Link>
                    )}

                    <button
                      type="button"
                      onClick={handleClearNotifications}
                      disabled={clearingNotifications}
                      style={{
                        ...styles.notificationAction,
                        justifyContent: "center",
                        background: "#101010",
                        borderStyle: "dashed",
                        opacity: clearingNotifications ? 0.75 : 1,
                      }}
                    >
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <Trash2 size={14} color="#f97316" />
                        {clearingNotifications ? "Limpiando..." : "Limpiar notificaciones"}
                      </span>
                    </button>

                    <div
                      style={{
                        marginTop: 8,
                        marginBottom: 4,
                        fontSize: 12,
                        color: "#9ca3af",
                        textTransform: "uppercase",
                        letterSpacing: ".04em",
                      }}
                    >
                      Actividad reciente
                    </div>

                    {loadingNotifications && (
                      <div style={{ color: "#9ca3af", fontSize: 13, padding: "8px 2px" }}>
                        Cargando notificaciones...
                      </div>
                    )}

                    {!loadingNotifications && notificationFeed.length === 0 && unreadMsgs === 0 && pendingFriends === 0 && pendingInvites.length === 0 && (
                      <div style={{ color: "#9ca3af", fontSize: 13, padding: "8px 2px" }}>
                        No tienes notificaciones nuevas.
                      </div>
                    )}

                    {!loadingNotifications &&
                      notificationFeed.map((item) => {
                        const type = String(item?.type || "").toLowerCase();
                        const key = item._id || `${item.type}-${item.createdAt || ""}`;
                        if (type === "session.invite") {
                          return (
                            <div
                              key={key}
                              style={{ ...styles.notificationFeedItem, cursor: "default" }}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                <ActivityTypeIcon type={item.type} />
                                <span style={{ fontSize: 13, color: "#fff", lineHeight: 1.35 }}>
                                  {describeNotificationActivity(item)}
                                </span>
                              </div>
                              <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>
                                {relativeTime(item.createdAt)}
                              </div>
                              <div style={{ display: "flex", gap: 8 }}>
                                <button
                                  type="button"
                                  onClick={() => handleSessionInviteResponse(item, true)}
                                  style={{
                                    borderRadius: 6,
                                    border: "1px solid #fcb14a",
                                    background: "#fcb14a",
                                    color: "#0b0d12",
                                    padding: "5px 12px",
                                    fontWeight: 700,
                                    cursor: "pointer",
                                    fontSize: 12,
                                  }}
                                >
                                  Aceptar
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleSessionInviteResponse(item, false)}
                                  style={{
                                    borderRadius: 6,
                                    border: "1px solid #3b4d64",
                                    background: "none",
                                    color: "#9fb2ca",
                                    padding: "5px 12px",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    fontSize: 12,
                                  }}
                                >
                                  Rechazar
                                </button>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <button
                            key={key}
                            type="button"
                            style={styles.notificationFeedItem}
                            onClick={() => handleNotificationItemClick(item)}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                              <ActivityTypeIcon type={item.type} />
                              <span style={{ fontSize: 13, color: "#fff", lineHeight: 1.35 }}>
                                {describeNotificationActivity(item)}
                              </span>
                            </div>
                            <div style={{ fontSize: 12, color: "#9ca3af" }}>
                              {relativeTime(item.createdAt)}
                            </div>
                          </button>
                        );
                      })}

                    <Link
                      to="/feed"
                      onClick={() => setNotificationsOpen(false)}
                      style={{
                        ...styles.notificationAction,
                        justifyContent: "center",
                        marginTop: 10,
                        marginBottom: 0,
                        background: "#121212",
                      }}
                    >
                      Ver feed completo
                    </Link>
                  </motion.div>
                )}
                </AnimatePresence>
              </div>

              {/* Botón avatar / menú usuario */}
              <button
                type="button"
                onClick={handleProfileAreaClick}
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  marginLeft: isMobile ? "16px" : "8px",
                  marginRight: isMobile ? "16px" : 0,
                }}
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
                aria-label={isMobile ? "Abrir menú de usuario" : "Ir a tu perfil"}
              >
                <img
                  src={getAvatarUrl(profile?.avatar)}
                  alt="avatar"
                  onError={(e) => { e.currentTarget.src = getAvatarUrl(null); }}
                  style={styles.avatarImg}
                />
                <span style={{ marginLeft: "6px", color: "#fff", fontSize: "12px" }}>
                  {userMenuOpen ? "▲" : "▼"}
                </span>
                {!isMobile && (
                  <span style={{ marginLeft: "8px", color: "#fff", display: "inline-flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.1, minWidth: 0 }}>
                    <span style={{ maxWidth: 132, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {profile?.username || "Usuario"}
                    </span>
                    {staffRoleLabel ? (
                      <span style={{ fontSize: 11, color: "#a9bad1", fontWeight: 700, marginTop: 2 }}>
                        {staffRoleLabel}
                      </span>
                    ) : null}
                  </span>
                )}
              </button>
            </div>

            {/* Dropdown del menú usuario */}
            <AnimatePresence>
            {userMenuOpen && (
              <motion.div
                key="user-menu"
                role="menu"
                style={{ ...styles.userMenu, transformOrigin: "top right" }}
                initial={{ opacity: 0, y: -8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -5, scale: 0.97 }}
                transition={{ type: "spring", duration: 0.22, bounce: 0 }}
              >
                <ul style={{ listStyle: "none", margin: 0, padding: 0, fontSize: 15, lineHeight: 1.6, whiteSpace: "nowrap" }}>
                  <li>
                    <Link to="/user-profile" onClick={() => setUserMenuOpen(false)}>Perfil</Link>
                  </li>
                  <li>
                    <Link to="/user-profile?tab=games" onClick={() => setUserMenuOpen(false)}>Librería</Link>
                  </li>
                  <li>
                    <Link to="/user-profile?tab=reviews" onClick={() => setUserMenuOpen(false)}>Reviews</Link>
                  </li>
                  <li>
                    <Link to="/user-profile?tab=lists" onClick={() => setUserMenuOpen(false)}>Listas</Link>
                  </li>
                  <li>
                    <Link to="/messages" onClick={() => setUserMenuOpen(false)}>
                      Mensajes {unreadMsgs > 0 && <span style={styles.badge}>{unreadMsgs}</span>}
                    </Link>
                  </li>

                  {isModerator && (
                    <>
                      <hr />
                      <li>
                        <Link to="/moderation" onClick={() => setUserMenuOpen(false)}>Moderación</Link>
                      </li>
                      {isAdmin && (
                        <li>
                          <Link to="/moderation/games" onClick={() => setUserMenuOpen(false)}>Admin</Link>
                        </li>
                      )}
                      <hr />
                    </>
                  )}

                  <li>
                    <Link to="/settings?section=account&tab=profile" onClick={() => setUserMenuOpen(false)}>Ajustes</Link>
                  </li>
                  <hr />

                  <li onClick={handleLogoutClick} style={{ color: "red", cursor: "pointer" }}>
                    Sign Out
                  </li>
                </ul>
              </motion.div>
            )}
            </AnimatePresence>
          </div>
        ) : (
          <ContextualAuthLink
            to="/login"
            style={{
              color: "#fff",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontWeight: "bold",
              fontSize: 14,
              padding: "6px 8px",
              borderRadius: 8,
              outline: "none",
              boxShadow: loginFocus ? "0 0 0 3px rgba(0,179,255,0.35)" : "none",
              transition: "box-shadow .18s ease",
            }}
            onFocus={() => setLoginFocus(true)}
            onBlur={() => setLoginFocus(false)}
          >
            <UserRound size={18} />
            <span>Login / Sign In</span>
          </ContextualAuthLink>
        )}
      </div>

      {/* --- BLOQUE MÓVIL --- */}
      <div style={styles.mobileBar}>
        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          style={styles.iconBtn}
          aria-label="Abrir buscador"
        >
          <Search size={18} style={styles.iconSvg} />
        </button>

        <div ref={mobileMenuWrapRef} style={{ position: "relative" }}>
          <button
            type="button"
            onClick={() => setMobileMenuOpen((v) => !v)}
            style={styles.iconBtn}
            aria-label="Abrir menú"
            aria-expanded={mobileMenuOpen}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="white" viewBox="0 0 24 24" style={{ display: "block" }}>
              <path d="M3 6h18v2H3zM3 11h18v2H3zM3 16h18v2H3z" />
            </svg>
          </button>

          <div style={styles.hamburgerMenu} aria-hidden={!mobileMenuOpen}>
            <ul style={styles.mobileNavList}>
              <li>
                <Link to="/search" onClick={() => setMobileMenuOpen(false)} style={styles.navLink}>
                  Juegos
                </Link>
              </li>
              <li>
                <Link to="/genres" onClick={() => setMobileMenuOpen(false)} style={styles.mobileSubNavLink}>
                  Géneros
                </Link>
              </li>
              <li>
                <Link to="/platforms" onClick={() => setMobileMenuOpen(false)} style={styles.mobileSubNavLink}>
                  Plataformas
                </Link>
              </li>
              {isAuthenticated && (
                <li>
                  <Link to="/mi-xp" onClick={() => setMobileMenuOpen(false)} style={styles.navLink}>
                    Mi XP
                  </Link>
                </li>
              )}
              <li>
                <Link to="/mundo-gaming" onClick={() => setMobileMenuOpen(false)} style={styles.navLink}>
                  Mundo Gaming
                </Link>
              </li>
              <li>
                <Link to="/communities" onClick={() => setMobileMenuOpen(false)} style={styles.navLink}>
                  Comunidad
                </Link>
              </li>
              <li>
                <Link to="/news" onClick={() => setMobileMenuOpen(false)} style={styles.navLink}>
                  Noticias
                </Link>
              </li>
              {isAuthenticated && (
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      openQuickSession();
                    }}
                    style={{
                      ...styles.navLink,
                      background: "transparent",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      font: "inherit",
                    }}
                  >
                    Registrar sesión
                  </button>
                </li>
              )}
              {/* Mensajes */}
              {isAuthenticated && (
                <li>
                  <Link to="/messages" onClick={() => setMobileMenuOpen(false)} style={styles.navLink}>
                    Mensajes {unreadMsgs > 0 && <span style={styles.badge}>{unreadMsgs}</span>}
                  </Link>
                </li>
              )}

              {isAuthenticated && isModerator && (
                <li>
                  <Link to="/moderation" onClick={() => setMobileMenuOpen(false)} style={styles.navLink}>
                    Moderación
                  </Link>
                </li>
              )}
              {isAuthenticated && isAdmin && (
                <li>
                  <Link to="/moderation/games" onClick={() => setMobileMenuOpen(false)} style={styles.navLink}>
                    Admin
                  </Link>
                </li>
              )}

              {!isAuthenticated && (
                <li>
                  <ContextualAuthLink to="/login" onClick={() => setMobileMenuOpen(false)} style={styles.navLink}>
                    Login / Sign In
                  </ContextualAuthLink>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* Overlay de búsqueda móvil */}
      <div style={styles.mobileSearchOverlay} role="dialog" aria-modal="true" aria-hidden={!searchOpen}>
        <div style={styles.mobileSearchCard}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
            type="text"
            placeholder="Buscar en XPHub..."
            value={searchQuery}
            onChange={handleInputChange}
            autoComplete="off"
            style={styles.mobileSearchInput}
            autoFocus
            aria-label="Buscar en XPHub"
          />
            <button
              type="button"
              onClick={() => {
                setSearchOpen(false);
                setSearchResults([]);
              }}
              style={styles.iconBtn}
              aria-label="Cerrar buscador"
            >
              <X size={18} style={{ display: "block" }} />
            </button>
          </div>

          {/* Enter → navegar a /search */}
          <form onSubmit={handleSubmit} style={{ display: "none" }}>
            <input type="submit" />
          </form>

          {loading && <p style={{ color: "#aaa", marginTop: 8 }}>Buscando...</p>}

          <div style={{ maxHeight: "55vh", overflowY: "auto", marginTop: 6 }}>
            {searchResults.map((game) => (
              <div
                key={game.id}
                style={styles.mobileResult}
                onClick={() => {
                  setSearchResults([]);
                  setSearchQuery("");
                  setSearchOpen(false);
                  openModal({
                    id: game.id,
                    name: game.name,
                    background_image: game.image,
                    source: "igdb",
                  });
                }}
              >
                <img
                  src={game.image}
                  alt={game.name}
                  style={{ width: 48, height: 48, borderRadius: 6, objectFit: "cover" }}
                />
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span style={{ color: "#fff", fontSize: 15, fontWeight: 600 }}>{game.name}</span>
                  <span style={{ color: "#999", fontSize: 13 }}>
                    {game.released ? `(${String(game.released).slice(0, 4)})` : ""}
                  </span>
                </div>
              </div>
            ))}
            {!loading && searchQuery.length >= 3 && searchResults.length === 0 && (
              <p style={{ color: "#888", padding: "6px 2px" }}>Sin resultados</p>
            )}
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            style={{ ...styles.iconBtn, width: "100%", marginTop: 10 }}
            aria-label="Buscar en resultados"
          >
            <Search size={18} style={styles.iconSvg} />
            <span style={{ marginLeft: 8 }}>Buscar en todo XPHub “{searchQuery.trim() || ""}”</span>
          </button>
        </div>
      </div>
      {/* Glow divider — línea fina azul/naranja en el borde inferior del header */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 1,
          background: "linear-gradient(90deg, transparent, rgba(0,179,255,0.75), rgba(252,177,74,0.5), transparent)",
          opacity: scrolled ? 1 : 0,
          transition: "opacity 260ms ease",
          pointerEvents: "none",
        }}
      />
    </header>
  );
}

export default Header;
